import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Loader2, Landmark, Vote, ShieldCheck, Flag } from "lucide-react";

const CAT_STYLES = {
  election: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
  milestone: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  compliance: "text-red-300 border-red-400/30 bg-red-400/10",
  campaign: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  fundraising: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
};
const CAT_ICONS = { election: Vote, milestone: Flag, compliance: ShieldCheck, campaign: CalendarDays, fundraising: CalendarDays };

export default function CampaignCalendar() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", category: "campaign" });

  const load = () => api.get("/political-os/calendar").then(r => setData(r.data)).catch(() => setData({ entity: null, events: [] }));
  useEffect(() => { load(); }, []);

  const addEvent = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    try {
      await api.post("/political-os/calendar/events", form);
      toast.success("Event added");
      setForm({ title: "", date: "", category: "campaign" });
      load();
    } catch (e) { toast.error(errMsg(e, "Could not add event")); }
  };

  const remove = async (id) => {
    try { await api.delete(`/political-os/calendar/events/${id}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not delete")); }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (!data.entity) {
    return (
      <div className="p-6 lg:p-8">
        <EmptyState icon={Landmark} title="No organization selected"
          description="Select your political entity and attach a race — the deadline calendar builds itself."
          action={<Btn onClick={() => navigate("/onboarding/political")}>Set up political entity</Btn>} />
      </div>
    );
  }

  const byMonth = {};
  data.events.forEach(e => {
    const m = e.date.slice(0, 7);
    (byMonth[m] = byMonth[m] || []).push(e);
  });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="campaign-calendar-page">
      <PageHeader eyebrow="Operations" title="Campaign Calendar"
        subtitle={`Deadlines auto-built from ${data.entity}'s race — election day, milestones, early voting and FEC reporting — plus your own events.`} />

      <Card data-testid="add-event-card">
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Event" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Direct mail artwork deadline" className="flex-1 min-w-[220px]" data-testid="event-title-input" />
          <Input label="Date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="event-date-input" />
          <Select label="Category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="event-category-select">
            {["campaign", "fundraising", "compliance", "election", "milestone"].map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
          <Btn onClick={addEvent} data-testid="add-event-btn"><Plus className="w-4 h-4" /> Add</Btn>
        </div>
      </Card>

      {Object.entries(byMonth).map(([month, events]) => (
        <Card key={month} padded={false} data-testid={`calendar-month-${month}`}>
          <div className="px-5 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-slate-400 font-medium">
            {new Date(month + "-02").toLocaleString("en-US", { month: "long", year: "numeric" })}
          </div>
          <div className="divide-y divide-white/5">
            {events.map(e => {
              const Icon = CAT_ICONS[e.category] || CalendarDays;
              return (
                <div key={e.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`calendar-event-${e.id}`}>
                  <div className="text-center w-12 shrink-0">
                    <div className="font-display text-xl font-bold text-white">{e.date.slice(8, 10)}</div>
                    <div className="text-[9px] uppercase text-slate-500">{new Date(e.date + "T12:00:00Z").toLocaleString("en-US", { weekday: "short" })}</div>
                  </div>
                  <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">{e.title}</div>
                    {e.source && <div className="text-[10px] text-slate-600">via {e.source}</div>}
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${CAT_STYLES[e.category] || "text-slate-400 border-white/15"}`}>{e.category}</span>
                  {e.days_away != null && e.days_away >= 0 && (
                    <span className="text-[11px] text-slate-500 w-16 text-right shrink-0">{e.days_away === 0 ? "today" : `in ${e.days_away}d`}</span>
                  )}
                  {!e.auto && (
                    <button onClick={() => remove(e.id)} data-testid={`delete-event-${e.id}`}
                      className="text-slate-600 hover:text-red-400 transition-colors shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
      {data.events.length === 0 && (
        <EmptyState icon={CalendarDays} title="No events yet" description="Attach a race to your entity to auto-build deadlines, or add your first event above." />
      )}
    </div>
  );
}
